import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation, useNavigationType, useSearchParams } from "react-router";
import type { PublicResident } from "@/shared/contracts";
import { ApiError } from "@/shared/api/errors";
import { env } from "@/shared/config/env";
import { queryKeys } from "@/shared/query/queryClient";
import { useServices } from "@/shared/services/registry";
import { routeAfterSession } from "@/shared/session/onboarding";
import { DataOriginBadge, ErrorState, Icon, LoadingState, Page, PlanetEventLine } from "@/shared/ui";
import { SameNameTag } from "@/shared/ui/SameNameTag";
import { sameNameTags } from "@/shared/ui/sameNameTags";
import { ResidentPortrait } from "./ResidentPortrait";
import { ResidentsDrawer } from "./ResidentsDrawer";
import { MapPortrait } from "./world/MapResidentCard";
import { PublicWorldMap } from "./world/PublicWorldMap";
import { DAILY_TITLE, DailySheet } from "./world/DailySheet";
import { GuestWorldStories, presenceSummary } from "./world/GuestWorldStories";
import { dailyStories, identityFaceCrop, storyOf } from "./world/guestStoriesModel";
import { NearPanel, type NearViewer } from "./world/NearPanel";
import { publicSky, stageStoryRank } from "./world/planetStageModel";
import "./world/guest-discovery.css";
import { unplaced, withImage } from "./world/publicMapModel";
import { usePublicMap } from "./world/usePublicMap";
import { agoText, extraPosts, hasOwnImage, isOriginalCharacter, nowLine, ORIGINAL_CHARACTER_TAG, presenceGroup, speciesName } from "./residentView";
import "./planet.css";

// 居民公开主页 2026-09-24 第二批整页重做，挪到 ResidentPage.tsx；从这里转出，路由（module.tsx）的引用不用改。
export { PublicPetPage } from "./ResidentPage";

/**
 * 一位居民：谁（照片或同物种插画、名字、物种、性格）、此刻在做什么、住在哪、梦想、最近一条公开小事。
 * “认识 TA”是卡片唯一的链接（整张卡都能点）；读屏先读到名字，链接名里也带着名字。
 * 名单抽屉（登录前后同一个）一行一位（UI-WORLD-LIVING-117-2，I 10-15：原来是方块头像、物种胶囊、带框按钮，像一张表）：圆的近景头像在左（访客用审过的头部框），
 * 物种跟在名字后面是一句小字；整行就是「认识 TA」，名字那一行右边一个小箭头（字留给读屏，链接名照旧带名字）。样子见 planet.css 的 --life-first。
 * 信息分层（I 10-16）：每行只留名字、物种、此刻、一句性格；住在、梦想、最近一件事在居民主页，这里不再列（上面第一行说的是原来的样子）。
 * 同名（SAME-NAME-LABEL-117，属主 6c2b 10-16 授权）：名单里两位居民名字一样时，名字旁各加「{城市}居民」，链接名里也带上，读屏分得开是哪一位。
 */
function ResidentCard({ resident, guest, tag }: { resident: PublicResident; guest: boolean; tag?: string }) {
  const titleId = `world-resident-${resident.pet_id}`;
  return (
    <li>
      <article className="ps-world-resident ps-world-resident--life-first" aria-labelledby={titleId} data-testid="resident-card">
        <div className="ps-world-resident__head">
          <ResidentPortrait name={resident.name} species={resident.species} photoUrl={resident.avatar_url} fallbackUrl={resident.fallback_avatar_url ?? null} official={resident.official_avatar === true} guestMedia={guest ? { petId: resident.pet_id, identity: resident.identity } : undefined} origin={resident.origin} size={56} focus={guest ? identityFaceCrop(resident) : null} />
          <div className="ps-world-resident__who">
            <div className="ps-world-resident__name">
              <h3 id={titleId}>{resident.name}<SameNameTag tag={tag} /></h3>
              <span className="ps-world-resident__species">{speciesName(resident.species)}</span>
              {/* 领养名单里按契约不会有原创角色；万一有，同样标出来（D5） */}
              {isOriginalCharacter(resident) ? <span className="ps-world-resident__species" data-resident-kind="public_npc">{ORIGINAL_CHARACTER_TAG}</span> : null}
            </div>
          </div>
        </div>
        <div className="ps-world-resident__life">
          <p className={`ps-world-resident__moment is-${presenceGroup(resident.presence)}`}>
            <span>此刻</span> {nowLine(resident)}
          </p>
          <p className="ps-world-resident__personality">{resident.personality}</p>
        </div>
        <Link className="ps-world-resident__more" to={`/world/residents/${encodeURIComponent(resident.pet_id)}${guest ? "?view=guest" : ""}`} aria-label={`认识 TA：${resident.name}${tag ? `，${tag}` : ""}`}>
          <span className="visually-hidden">认识 TA</span><Icon name="chevron" size={18} />
        </Link>
      </article>
    </li>
  );
}

const JOIN_ID = "world-join";

/**
 * 一位居民都不显示时的标题（c84a D1146-5）：如实说此刻没有可以见面的居民。不说“正在搬来”——合同里没有“正在入驻”的状态，
 * 名单空也可能是没有合格的公开居民、被撤回；不露审核、费用这类内部原因。
 */
const WORLD_EMPTY_TITLE = "这里暂时还没有可以见面的居民";

/**
 * 访客的加入入口（c84a D0842-2：加入和登录收成一个紧凑的次级入口）：顶栏右边一个“加入星球”胶囊，点开一个小弹层两项——
 * 寻找我的 TA（注册，契约给了 own_pet 才有）、已经有 TA 了？登录；写给访客的那句话也收在这里。
 * Esc 关上并把焦点还给胶囊；点弹层外面也关。共享次级按钮的配色，不用叶绿、深绿。
 */
function JoinEntry({ canRegister }: { canRegister: boolean }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      buttonRef.current?.focus({ preventScroll: true });
    };
    const onPointer = (event: Event) => {
      if (event.target instanceof Node && rootRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);
  return (
    <div className="ps-pmap-join" ref={rootRef}>
      <button ref={buttonRef} type="button" className="ps-btn ps-btn--secondary ps-pmap-join__btn" aria-expanded={open} aria-controls={JOIN_ID} onClick={() => setOpen((v) => !v)}>
        加入星球
      </button>
      <div id={JOIN_ID} className="ps-pmap-join__pop" hidden={!open} data-testid="join-pop">
        {canRegister ? (
          <Link className="ps-btn ps-btn--primary ps-pmap-join__item" to="/register">
            寻找我的 TA
          </Link>
        ) : null}
        <Link className="ps-btn ps-btn--secondary ps-pmap-join__item" to="/login">
          已经有 TA 了？登录
        </Link>
        <p className="ps-pmap-join__note">注册不会自动领养，也不会自动加入别人的家；每一步都由你确认。</p>
      </div>
    </div>
  );
}

/**
 * 访客星球（/world，不登录也能看）。2026-10-03 起按 c84a CR-PUBLIC-WORLD-MAP-01：整屏是地图（./world/PublicWorldMap），
 * 看出居民分别生活在哪、此刻在哪，点居民才出就近面板（117-2；117-2b 起登录前后都是它）。第四轮（c84a D0842-2）收紧：
 * - 底部不再常驻“标题 + 计数 + 加入/登录”那一大块；地图下沿只有新鲜事一条（可折叠）；
 * - 人数收成顶栏里的小胶囊（“14 位居民”），点开是居民名单抽屉；抽屉头里三个数分开说：此刻居民共 N 位（/public/world）、
 *   在地图上显示公开位置的 M 位、暂时不知道在哪的 K 位（这两个来自 /public/world/residents，分页合并去重，只算有形象的）；
 * - 访客的加入与登录收成顶栏右边一个“加入星球”胶囊；已登录的人那里换成回家的入口。
 * 页面标题（h1）留给读屏，不再占屏。只读：打开页面不推进世界、不调模型、不发写请求。
 * 删插画（6c2b-no-illustration，10-03 用户「没有形象的都不要出现在星球了」）：live 下名单只显示有自己形象的居民，
 * 标题、人数胶囊、抽屉头的“此刻居民共 N 位”都按显示的算；被藏起来的居民写的小事不列；地图图层原来就只画有形象的（withImage）。
 */
export function PublicWorldPage() {
  const [params] = useSearchParams();
  const guest = params.get("view") === "guest";
  const { pets, session: sessionService } = useServices();
  const session = useQuery({ queryKey: queryKeys.session, queryFn: () => sessionService.current(), enabled: !guest, staleTime: 30_000 });
  const memberId = !guest && session.isSuccess && session.data.authenticated && session.data.user?.user_id.trim()
    ? session.data.user.user_id : null;
  const fixtureDefault = !guest && env.dataMode === "fixture";
  const knownAnonymous = !guest && session.isSuccess && !session.data.authenticated;
  const worldEnabled = guest || fixtureDefault || Boolean(memberId) || knownAnonymous;
  // 显式游客与已确认成员成对选择方法/缓存；身份未确认时不借旧世界资料。
  const world = useQuery({
    queryKey: guest ? queryKeys.publicWorldGuest : fixtureDefault || knownAnonymous ? queryKeys.publicWorld
      : memberId ? queryKeys.publicWorldMember(memberId) : ["public", "world", "unconfirmed"],
    queryFn: () => {
      if (!guest) return pets.publicWorld();
      if (!pets.publicWorldGuest) throw ApiError.capability("public.guest_world_read");
      return pets.publicWorldGuest();
    },
    enabled: worldEnabled, staleTime: 30_000,
    ...(guest ? { retry: false, refetchOnWindowFocus: false, refetchOnReconnect: false } : {}),
  });
  const identityError = !guest && !fixtureDefault
    ? session.isError ? session.error : session.isSuccess && session.data.authenticated && !memberId
      ? ApiError.capability("public.member_identity", "暂时无法确认你的身份，请重试。") : null
    : null;
  const worldError = identityError ?? world.error;
  const worldFailed = Boolean(worldError);
  const worldPending = !worldFailed && world.isPending;
  const worldData = worldEnabled && !worldFailed ? world.data : undefined;
  const map = usePublicMap();
  const [drawer, setDrawer] = useState<{ which: "all" | "unknown"; from: HTMLElement | null } | null>(null);
  const signedIn = !guest && env.dataMode === "live" && Boolean(memberId);
  // 就近面板谁在看（117-2b）：和居民主页同一套身份口径，TA 的话读同一个缓存键（进「认识 TA」不再重读）；身份还没确认时不读
  const viewer: NearViewer = guest ? { kind: "guest" } : memberId ? { kind: "member", userId: memberId } : knownAnonymous ? { kind: "anonymous" } : { kind: "pending" };
  // 只显示有自己形象的居民（用户 10-03「没有形象的都不要出现在星球了」「图像上的插画给删除掉！」）：标题、人数、最近的小事都按显示的算。演示模式照旧（演示居民用演示图）
  const everyone = worldData?.residents ?? [];
  const residents = env.dataMode === "live" ? everyone.filter(hasOwnImage) : everyone;
  // 被藏起来的居民（没有自己的形象）写的小事也不列；玩家自家公开宠物等别的作者照旧
  const hiddenIds = new Set(everyone.filter((resident) => !residents.includes(resident)).map((resident) => resident.pet_id));
  // 名单抽屉里都是驿站居民（PublicResident）：同名的各带城市（SAME-NAME-LABEL-117）
  const drawerSameName = sameNameTags(residents.map((resident) => ({ id: resident.pet_id, name: resident.name, resident: true, city: resident.city })));
  const canRegister = worldData?.entries.some((entry) => entry.route === "own_pet") ?? false;
  const back = signedIn && session.data ? routeAfterSession(session.data) : guest ? "/welcome?view=guest" : "/welcome";
  const stillLooking = signedIn && session.data?.onboarding?.step === "needs_companion";
  const serverNow = worldData ? Date.parse(worldData.server_time) : Number.NaN;
  const nowMs = Number.isFinite(serverNow) ? serverNow : Date.now();
  // 名单行不再列最近一件事（117-2，I 10-16），小事都在这里；只去掉同一位作者一字不差的重复
  const morePosts = worldData ? extraPosts(worldData.recent_posts.filter((post) => !(post.author.actor_kind === "pet" && hiddenIds.has(post.author.actor_id))), []) : [];
  // 只算有形象的居民（6c2b-no-illustration：没形象的不显示），和地图上画的一致
  const mapResidents = withImage(map.data?.residents ?? []);
  const unknown = unplaced(mapResidents);
  const placedCount = mapResidents.length - unknown.length;
  const openAll = (from: HTMLElement | null) => setDrawer({ which: "all", from });
  const openUnknown = (from: HTMLElement | null) => setDrawer({ which: "unknown", from });
  const [dailyLead, setDailyLead] = useState<string | null>(null);
  // 这一次进「先逛逛」算不算从居民主页回来（117-2，补 117-1 记下的小洞）：浏览器后退到这一页，或者居民主页的「返回」带着标记；
  // 别的路（欢迎页「跳过」、点链接进来）都不算，面板一律收着落地。「回来接着看」的记号点进主页时才记、挂上就用掉，新开这一页时一般没有
  // 117-2b 起登录后也一样：点「认识 TA」前开着的那张就近面板，回来时重新打开（I 10-16）
  const navigationType = useNavigationType();
  const location = useLocation();
  const returning = (location.state as { fromResident?: unknown } | null)?.fromResident === true || navigationType === "POP";
  // 形象撤回、缺失的不进故事（面板和就近面板同一份）
  const deniedIds = new Set(map.data?.residents.filter(r => r.identity?.official_state === "revoked" || r.identity?.official_state === "missing").map(r => r.pet_id) ?? []);
  // 人数按显示的算（删插画包：不再用接口的 living_residents），标题、人数胶囊、抽屉头同一个数
  const total = worldData ? residents.length : null;
  const title = worldPending
    ? "星球正在亮起来…"
    : worldFailed
      ? "暂时看不到星球上的居民"
      : residents.length
        ? `此刻有 ${total} 位居民在星球上生活`
        : WORLD_EMPTY_TITLE;
  const pill = worldPending ? "正在数居民…" : worldFailed ? "居民名单没读出来" : total ? `${total} 位居民` : "还没有居民";
  const mapPartial = Boolean(map.data && !map.data.complete);
  // 三个数分开说，各说各的来源，不硬凑成一句
  const tallyMap = map.isPending ? "地图上的位置还在读" : map.isError ? "地图上的位置暂时没读出来" : `在地图上显示公开位置的 ${placedCount} 位${mapPartial ? "（只读到一部分）" : ""}`;

  // 演示角标单拿出来：地区层不放世界人数胶囊，角标照留（UI-REVIEW110-02）
  const badge = worldData ? <DataOriginBadge origin={worldData.data_origin} label="演示居民" /> : null;
  const count = (
    <>
      <button type="button" className="ps-pmap-pill" aria-haspopup="dialog" aria-expanded={drawer?.which === "all"} onClick={(event) => openAll(event.currentTarget)}>
        {pill}
      </button>
      {badge}
    </>
  );
  const entry = signedIn ? (
    <Link className="ps-btn ps-btn--secondary ps-pmap-join__btn" to={back}>
      {stillLooking ? "继续寻找我的 TA" : "回到我的家"}
    </Link>
  ) : (
    <JoinEntry canRegister={canRegister} />
  );
  const emptyAction = signedIn ? null : <>
    {canRegister ? <Link className="ps-pmap__state-btn" to="/register">寻找我的 TA</Link> : null}
    <Link className="ps-pmap__state-btn is-quiet" to="/login">已经有 TA 了？登录</Link>
  </>;

  return (
    <Page bare className={`ps-world-page ps-pmap-page ps-night${guest ? " ps-guest-discovery" : ""}`}>
      {/* 页面标题留给读屏（c84a D0842-2：大标题不再常驻占屏），人数在顶栏胶囊和名单抽屉里 */}
      <h1 id="world-title" className="visually-hidden">
        {title}
      </h1>
      <PublicWorldMap
        back={back}
        layer={map.data}
        pending={map.isPending}
        failed={map.isError}
        onRetry={() => void map.refetch()}
        onOpenAll={residents.length ? openAll : null}
        onOpenUnknown={unknown.length ? openUnknown : null}
        worldEmpty={Boolean(worldData) && residents.length === 0}
        count={count}
        badge={badge}
        entry={entry}
        emptyAction={emptyAction}
        externalOverlayOpen={Boolean(drawer)}
        discovery={guest ? ({ feed, feedPending, feedFailed, onRetryFeed, count: frameCount, cardOpen, closeCard, scope }) => {
          // 「今天星球上发生的事」（UI-WORLD-LIVING-117-1）：贴在星球底边的上拉面板，把手上说此刻和最上面那件事，拉起来是 GuestWorldStories；
          // 星球上开着短卡、选城卡时面板让开（评审 R2 第 1 条）；让开时把手被按下，请地图把卡关上（R3 第 1 条）
          // 城市层（117-2，I 10-15：在哪一层说哪一层的今天）：标题、此刻几位在家几位在外面、故事都只说这座城那几位；这一层的点还在读时先说正在看。
          // 全星球的人数按钮只在地球总览上挂（城市层顶栏已经说了这座城几位，面板里列的就是这几位）
          const ready = !worldPending && !worldError;
          const reading = Boolean(scope && !scope.ids);
          const here = scope?.ids ? residents.filter(r => scope.ids!.has(r.pet_id)) : residents;
          return <DailySheet count={scope ? null : frameCount} yielded={cardOpen} onReclaim={closeCard} title={scope ? `今天${scope.label}发生的事` : DAILY_TITLE} layer={scope?.key} returnable={returning}
            summary={ready && !reading ? presenceSummary(here) : null}
            peek={ready && !reading ? dailyLead : worldPending || reading ? `正在看${scope ? scope.label : "星球上"}的今天…` : "暂时看不到星球上的居民"}>
            <GuestWorldStories
              residents={residents} deniedIds={deniedIds}
              feed={feed} feedPending={feedPending} feedFailed={feedFailed} onRetryFeed={onRetryFeed}
              pending={worldPending || reading} error={worldError} onRetryWorld={() => void world.refetch()}
              canRegister={canRegister} nowMs={nowMs} onPeek={setDailyLead} scope={scope?.ids ?? null}
            />
          </DailySheet>;
        } : undefined}
        nearReturn={returning}
        nearCard={({ resident, cardRef, onClose, onBack, feed, onLeave }) => {
          // 点一只＝就近看 TA（117-2；117-2b 起登录后也是它，I 10-16）：名单里同一位给性格、睡没睡；最近一件事是故事排序里 TA 最靠前的那一条（和面板同一套分档、同一个新鲜事读口）
          const story = !worldPending && !worldError ? storyOf(dailyStories(residents, feed && feed.layer_ready !== false ? feed.items : [], null, deniedIds), resident.pet_id) : null;
          return <NearPanel viewer={viewer} mapResident={resident} resident={residents.find(r => r.pet_id === resident.pet_id) ?? null} story={story} nowMs={nowMs} cardRef={cardRef} onClose={onClose} onBack={onBack} onLeave={onLeave} />;
        }}
        // 访客「先逛逛」先落在插画星球（WORLD-ART-117，I 10-16）：今天有新鲜事的先上图，名次和面板、就近面板同一套故事排序、同一个新鲜事读口；名单还没读到时谁都不算有新鲜事
        // 星球的昼夜（B PUBLIC-SKY-117）：/public/world 给的公开天色，契约生成物等 I 重生，先按附加字段读；名单还在读时先不下判断（免得读到以后昼夜翻一次）
        planetSky={guest ? { pending: worldPending, sky: publicSky((worldData as { sky?: unknown } | undefined)?.sky) } : undefined}
        stageRank={guest ? (feed) => stageStoryRank(!worldPending && !worldError ? dailyStories(residents, feed && feed.layer_ready !== false ? feed.items : [], null, deniedIds) : { stories: [] }) : undefined}
      />
      <ResidentsDrawer open={drawer?.which === "all"} onClose={() => setDrawer(null)} returnFocusTo={drawer?.which === "all" ? drawer.from : null} titleId="world-residents-title" title="星球上的居民">
        <ul className="ps-pmap-tally ps-pmap-tally--compact" aria-label="星球上的人数" data-testid="world-tally">
          <li>{worldPending ? "正在数居民…" : worldFailed ? "居民名单没读出来" : `此刻居民共 ${total} 位`}</li>
          <li>{tallyMap}</li>
          {/* 暂时不知道在哪的为 0 时不显示这一行，大于 0 才说（I 10-16；三个数照旧分开说，c84a D0842-2） */}
          {map.data && unknown.length ? (
            <li>
              <span>暂时不知道在哪的 {unknown.length} 位</span>
              <button type="button" className="ps-pmap-tally__btn" aria-label={`看看暂时不知道在哪的 ${unknown.length} 位`} onClick={() => setDrawer({ which: "unknown", from: drawer?.from ?? null })}>
                看看是谁
              </button>
            </li>
          ) : null}
        </ul>
        {worldPending ? (
          <LoadingState lines={3} label="正在看星球上的今天…" />
        ) : worldFailed ? (
          <ErrorState error={worldError} onRetry={() => void (identityError ? session.refetch() : world.refetch())} />
        ) : residents.length ? (
          /* 读屏的标题层级：抽屉标题 h2 → 每位居民 h3（卡片照旧） */
          <ul className="ps-world-residents" id="residents" aria-labelledby="world-residents-title">
            {residents.map((resident) => (
              <ResidentCard key={resident.pet_id} resident={resident} guest={guest} tag={drawerSameName.get(resident.pet_id)} />
            ))}
          </ul>
        ) : (
          // 访客看到可以先寻找自己的 TA；已登录的人看到“有居民可以见面时会出现”（D1146-5：不暗示有人正在来）
          <p className="ps-world-empty">{signedIn ? "有居民可以见面时，会出现在这里。" : "你也可以先寻找自己的 TA，带 TA 来星球上生活。"}</p>
        )}
        {morePosts.length ? (
          <section className="ps-world-posts" aria-labelledby="world-posts-title">
            <h3 id="world-posts-title">星球上最近的小事</h3>
            <ul>
              {morePosts.map((post) => (
                <li key={post.post_id}>
                  <strong>{post.author.display_name}</strong>
                  <p>{post.text}</p>
                  <PlanetEventLine event={post.planet_event} />
                  <small>{agoText(post.created_at, nowMs)}</small>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </ResidentsDrawer>
      {unknown.length ? (
        <ResidentsDrawer
          open={drawer?.which === "unknown"}
          onClose={() => setDrawer(null)}
          returnFocusTo={drawer?.which === "unknown" ? drawer.from : null}
          titleId="world-unknown-title"
          title="暂时不知道在哪的居民"
          closeLabel="收起"
          testId="unknown-drawer"
        >
          <p className="ps-pmap-unknown__note">这几位此刻在哪还说不准；有了公开的大约位置，就会出现在地图上。</p>
          <ul className="ps-pmap-unknown" aria-labelledby="world-unknown-title">
            {unknown.map((resident) => (
              <li key={resident.pet_id}>
                <span aria-hidden="true">
                  {guest ? <ResidentPortrait name={resident.name} species={resident.species} photoUrl={resident.avatar_url || resident.identity?.url || null} official guestMedia={{ petId: resident.pet_id, identity: resident.identity }} size={48} surface="paper" /> : <MapPortrait resident={resident} size={48} />}
                </span>
                <div className="ps-pmap-unknown__who">
                  <strong>{resident.name}</strong>
                  <small>{nowLine(resident)}</small>
                </div>
                <Link className="ps-pmap-unknown__more" to={`/world/residents/${encodeURIComponent(resident.pet_id)}${guest ? "?view=guest" : ""}`} aria-label={`认识 TA：${resident.name}`}>
                  <span className="visually-hidden">认识 TA</span><Icon name="chevron" size={18} />
                </Link>
              </li>
            ))}
          </ul>
        </ResidentsDrawer>
      ) : null}
    </Page>
  );
}
